import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { EditOutlined, DeleteOutlined, CheckOutlined, MessageOutlined, MoreOutlined } from '@ant-design/icons';
import { renderMarkdown } from '../lib/markdown';
import { parseFileLinks } from '../lib/file-links';
import { UserAvatar } from './UserAvatar';
import HeartbeatIcon from './HeartbeatIcon';
import { UserName } from './UserName';
import ReactionBar from './ReactionBar';
import ReactionAddButton from './ReactionAddButton';
import EditEditor from './EditEditor';
import FileLink from './FileLink';
import TaskStatusPill from './TaskStatusPill';
import * as api from '../lib/api';
import SystemMessageBubble, { isBPP32GrantPayload } from './SystemMessageBubble';
import { isSystemMessage, isTaskAssignment, isTaskHeartbeat } from '../lib/message-types';
import { useMessageStore, useThreadStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { useLongPress } from '../hooks/useLongPress';
import CommandResultCard from './CommandResultCard';
import { AttachmentGrid } from './AttachmentGrid';
import { trackCommand, getCommandStatus } from '../hooks/useCommandTracking';
import type { Message } from '../types';
import type { ChannelMember } from '../lib/api';
import { isAllowedAttachmentURL } from '../lib/attachment-url';

interface Props {
  message: Message;
  userMap: Map<string, string>;
  members: ChannelMember[];
  memberMap: Map<string, ChannelMember>;
  currentUserId?: string;
  currentUserRole?: string;
  onRetry?: (message: Message) => void;
  onThreadOpen?: (messageId: string) => void;
  isThreadActive?: boolean;
}

// borgee #1108 F5: shared allowlist for image content_type bodies — http(s)://
// (any case) OR a same-origin relative path with a single leading '/' (NOT '//',
// which is protocol-relative and unsafe). Identical to the server guard
// (store.IsAllowedImageContentURL). Used by both the render guard (ImageContent)
// and the edit-save guard so the UI rejects editing an image message to a
// javascript:/data:/protocol-relative scheme instead of sending it.
export const isAllowedImageContentURL = isAllowedAttachmentURL;

function MessageItem({ message, userMap, members, memberMap, currentUserId, currentUserRole, onRetry, onThreadOpen, isThreadActive }: Props) {
  const editMessage = useMessageStore(s => s.editMessage);
  // The row owns its own thread lookup: the store is keyed by origin message, so
  // this is an O(1) read that changes identity only when THIS message's thread
  // does. Threading it down as a prop would re-render every row in the channel
  // whenever any one thread's reply count moved.
  const thread = useThreadStore(s => s.byMessage[message.id]);
  const taskSnapshot = useMemo(() => {
    if (message.content_type !== 'task') return null;
    try { return JSON.parse(message.content) as { task_id: string; seq?: number; title: string; status: string; assignee_id?: string }; }
    catch { return null; }
  }, [message.content, message.content_type]);
  const liveTask = useTaskStore(s => taskSnapshot ? s.byTaskId[taskSnapshot.task_id] : undefined);
  const taskData = liveTask ?? (taskSnapshot ? {
    id: taskSnapshot.task_id,
    seq: taskSnapshot.seq ?? 0,
    title: taskSnapshot.title,
    status: taskSnapshot.status as 'open' | 'in_progress' | 'done' | 'cancelled',
    assignee_id: taskSnapshot.assignee_id,
    assignee: undefined,
  } : null);
  const isSystem = isSystemMessage(message);
  const senderName = isSystem ? 'System' : (message.sender_name ?? userMap.get(message.sender_id) ?? 'Unknown');
  const isOwn = message.sender_id === currentUserId;
  const isAdmin = currentUserRole === 'admin';
  const isDeleted = !!message.deleted_at;
  const time = formatTime(message.created_at);

  const [editing, setEditing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const deleteConfirmRef = useRef<HTMLDivElement>(null);
  const actionSheetRef = useRef<HTMLDivElement>(null);
  const cancelDelete = useCallback(() => setShowDeleteConfirm(false), []);
  const closeActionSheet = useCallback(() => setMobileActionsOpen(false), []);
  useDismissibleLayer({ active: showDeleteConfirm, within: deleteConfirmRef, onEscape: cancelDelete });
  useDismissibleLayer({ active: mobileActionsOpen, within: actionSheetRef, onEscape: closeActionSheet });

  const commandData = useMemo(() => {
    if (message.content_type !== 'command') return null;
    try { return JSON.parse(message.content) as { command: string; params?: string }; }
    catch { return null; }
  }, [message.content, message.content_type]);

  const [commandStatus, setCommandStatus] = useState(() => getCommandStatus(message.id));

  useEffect(() => {
    if (message.content_type !== 'command') return;
    trackCommand(message.id);
    const interval = setInterval(() => {
      setCommandStatus(getCommandStatus(message.id));
    }, 1000);
    return () => clearInterval(interval);
  }, [message.id, message.content_type]);

  const renderedContent = useMemo(() => {
    if (message.content_type === 'image' || message.content_type === 'command' || message.content_type === 'task') return null;
    return renderMarkdown(message.content, message.mentions, userMap);
  }, [message.content, message.content_type, message.mentions, userMap]);


  const senderUser = memberMap.get(message.sender_id);
  const agentId = senderUser?.role === 'agent' ? message.sender_id : null;

  const startEdit = useCallback(() => {
    setEditError(null);
    setEditing(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditError(null);
    setEditing(false);
  }, []);

  const saveEdit = useCallback(async (newContent: string) => {
    const trimmed = newContent.trim();
    if (!trimmed || trimmed === message.content) {
      cancelEdit();
      return;
    }
    // borgee #1108 F5 (edit rail): an image message must stay an http(s) URL or
    // same-origin path — the server preserves content_type on edit and rejects
    // a bad scheme with 400 INVALID_CONTENT. Mirror that guard here so the UI
    // surfaces the error instead of round-tripping a doomed request.
    if (message.content_type === 'image' && !isAllowedImageContentURL(trimmed)) {
      setEditError('图片链接必须是 http(s):// 网址或同源路径');
      return;
    }
    setEditError(null);
    setEditSaving(true);
    try {
      const updated = await api.editMessage(message.id, trimmed);
      editMessage(message.channel_id, updated);
      setEditing(false);
    } catch {
      // keep editing on failure
    } finally {
      setEditSaving(false);
    }
  }, [message, editMessage, cancelEdit]);

  const handleDelete = useCallback(async () => {
    try {
      await api.deleteMessage(message.id);
    } catch {
      // ignore
    }
    setShowDeleteConfirm(false);
  }, [message.id]);

  const canEdit = isOwn && !isDeleted && !message._pending && !message._failed;
  const canDelete = (isOwn || isAdmin) && !isDeleted && !message._pending && !message._failed;
  // gh#686 §4 #4 #5: 只有当消息没 reaction 时, 才把 + 加到 .message-actions
  // 工具栏 (跟 edit/delete 一组). 已经有 reaction 时, ReactionBar 自带 +
  // 在那一行不重复出现. 这样无 reaction 状态消息容器不撑高.
  const hasReactions = (message.reactions?.length ?? 0) > 0;
  const canAddReaction =
    !isDeleted &&
    !message._pending &&
    !message._failed &&
    !hasReactions &&
    !!currentUserId;
  const canOpenThread = !!onThreadOpen && !isDeleted && !message._pending && !message._failed;
  const hasActions = canEdit || canDelete || canAddReaction || canOpenThread;

  const openThread = useCallback(() => {
    onThreadOpen?.(message.id);
  }, [onThreadOpen, message.id]);

  const openActionSheet = useCallback(() => {
    if (hasActions) setMobileActionsOpen(true);
  }, [hasActions]);

  const longPressHandlers = useLongPress(openActionSheet);

  if (isSystem) {
    // CM-onboarding + BPP-3.2.2 system DM render path. Delegated to
    // SystemMessageBubble so it can be unit-tested without an AppContext
    // provider. Detection: parse quick_action JSON; if shape matches
    // BPP-3.2 GrantPayload (action ∈ grant/reject/snooze + 4 fields),
    // render three buttons; else fall back to CM-onboarding single-button.
    let parsed: unknown = null;
    if (message.quick_action) {
      try { parsed = JSON.parse(message.quick_action); } catch { parsed = null; }
    }
    const bpp32 = isBPP32GrantPayload(parsed) ? parsed : null;
    const fallback = (!bpp32 && parsed && typeof parsed === 'object')
      ? (parsed as { kind?: string; label?: string; action?: string })
      : null;
    return (
      <SystemMessageBubble
        bodyHTML={renderedContent!}
        bpp32={bpp32}
        fallback={fallback}
      />
    );
  }

  if (isTaskHeartbeat(message) && !isDeleted) {
    return (
      <div className="message-heartbeat" data-message-type="task_heartbeat">
        <HeartbeatIcon size={16} active={false} />
        <span>Heartbeat: status update requested</span>
        <span className="message-heartbeat-time">{time}</span>
      </div>
    );
  }

  // A task assignment is an event about the task, not its author speaking, so
  // it renders as a thread notice with no avatar or byline.
  if (isTaskAssignment(message) && !isDeleted) {
    return (
      <div className="message-task-assignment" data-message-type="task_assignment">
        <div
          className="message-task-assignment-body"
          dangerouslySetInnerHTML={{ __html: renderedContent! }}
        />
        <div className="message-task-assignment-rule" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={`message-item ${isOwn ? 'message-own' : ''} ${isThreadActive ? 'message-thread-active' : ''}`} {...longPressHandlers}>
      <UserAvatar userId={message.sender_id} displayName={senderName} size={36} />
      <div className="message-body">
        <div className="message-header">
          <span className="message-sender"><UserName displayName={senderName} role={senderUser?.role} /></span>
          <span className="message-time">{time}</span>
          {message.edited_at && !isDeleted && (
            <span className="message-edited" title={`Edited at ${formatTime(message.edited_at)}`}>
              (edited)
            </span>
          )}
          {isOwn && (
            <span className="message-delivery-status">
              {message._pending && '⏳'}
              {message._failed && (
                <>
                  ❌
                  {onRetry && (
                    <button className="retry-btn" onClick={() => onRetry(message)}>重试</button>
                  )}
                </>
              )}
              {!message._pending && !message._failed && <span className="delivery-check"><CheckOutlined /></span>}
            </span>
          )}
        </div>
        <div className="message-content">
          {isDeleted ? (
            <div className="message-deleted">此消息已删除</div>
          ) : editing ? (
            <>
              <EditEditor
                initialContent={message.content}
                onSave={saveEdit}
                onCancel={cancelEdit}
                disabled={editSaving}
                users={members}
              />
              {editError && (
                <div className="message-edit-error" role="alert">{editError}</div>
              )}
            </>
          ) : message.content_type === 'command' && commandData ? (
            <div className="message-command">
              <div className="message-command-label">⚡ {commandData.command}{commandData.params ? ` ${commandData.params}` : ''}</div>
              {commandStatus && <CommandResultCard status={commandStatus} />}
            </div>
          ) : message.content_type === 'image' ? (
            <ImageContent url={message.content} />
          ) : message.content_type === 'task' && taskData ? (
            <MessageTextWithFileLinks html={renderMarkdown(taskData.title, message.mentions, userMap)} rawContent={taskData.title} agentId={agentId} />
          ) : (
            <MessageTextWithFileLinks html={renderedContent!} rawContent={message.content} agentId={agentId} />
          )}
          {/* Multi-image attachments (Discord-style grid) */}
          {message.attachments && message.attachments.length > 0 && (
            <AttachmentGrid attachments={message.attachments} />
          )}
        </div>
        {!isDeleted && !editing && (
          <ReactionBar
            reactions={message.reactions ?? []}
            channelId={message.channel_id}
            messageId={message.id}
            currentUserId={currentUserId}
            userMap={userMap}
          />
        )}
        {(thread || taskData) && (
          <div className="message-task-thread-row">
            {taskData && (
              <TaskStatusPill
                task={taskData}
                assigneeName={taskData.assignee?.display_name}
              />
            )}
            {thread && onThreadOpen && (
              <div className="thread-preview-bar" onClick={() => onThreadOpen(message.id)}>
                <MessageOutlined className="thread-preview-icon" />
                <span className="thread-preview-count">{thread.message_count} {thread.message_count === 1 ? 'reply' : 'replies'} ›</span>
              </div>
            )}
          </div>
        )}
      </div>
      {!editing && hasActions && (
        <>
          <div className="message-actions">
            {canAddReaction && currentUserId && (
              <ReactionAddButton
                channelId={message.channel_id}
                messageId={message.id}
                currentUserId={currentUserId}
                variant="toolbar-btn"
              />
            )}
            {canOpenThread && (
              <button className="message-action-btn" onClick={openThread} title="Thread">
                <MessageOutlined />
              </button>
            )}
            {canEdit && (
              <button className="message-action-btn" onClick={startEdit} title="Edit">
                <EditOutlined />
              </button>
            )}
            {canDelete && (
              <button className="message-action-btn message-action-delete" onClick={() => setShowDeleteConfirm(true)} title="Delete">
                <DeleteOutlined />
              </button>
            )}
          </div>
          {/* Whether this is visible is the stylesheet's call, not React's: it
              appears only for a pointer that cannot hover the toolbar above. */}
          <button
            className="message-actions-trigger"
            onClick={openActionSheet}
            title="Message actions"
            aria-label="Message actions"
          >
            <MoreOutlined />
          </button>
        </>
      )}
      {showDeleteConfirm && (
        <div ref={deleteConfirmRef} className="message-delete-overlay" onClick={cancelDelete}>
          <div className="message-delete-dialog" onClick={e => e.stopPropagation()}>
            <p>确定删除这条消息？</p>
            <div className="message-delete-dialog-actions">
              <button className="btn-cancel" onClick={cancelDelete}>取消</button>
              <button className="btn-danger" onClick={handleDelete}>删除</button>
            </div>
          </div>
        </div>
      )}
      {mobileActionsOpen && (
        <div ref={actionSheetRef} className="mobile-action-sheet-overlay" onClick={closeActionSheet}>
          <div className="mobile-action-sheet" onClick={e => e.stopPropagation()}>
            {canAddReaction && currentUserId && (
              <ReactionAddButton
                channelId={message.channel_id}
                messageId={message.id}
                currentUserId={currentUserId}
                variant="sheet-row"
              />
            )}
            {canOpenThread && (
              <button className="mobile-action-sheet-btn" onClick={() => { setMobileActionsOpen(false); openThread(); }}>
                <MessageOutlined /> Thread
              </button>
            )}
            {canEdit && (
              <button className="mobile-action-sheet-btn" onClick={() => { setMobileActionsOpen(false); startEdit(); }}>
                <EditOutlined /> Edit
              </button>
            )}
            {canDelete && (
              <button className="mobile-action-sheet-btn mobile-action-sheet-danger" onClick={() => { setMobileActionsOpen(false); setShowDeleteConfirm(true); }}>
                <DeleteOutlined /> Delete
              </button>
            )}
            <button className="mobile-action-sheet-btn mobile-action-sheet-cancel" onClick={closeActionSheet}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoised because the row now subscribes to its own thread: without this, a
// re-render of the surrounding list would still re-render every row and undo
// the point of the per-row subscription.
export default React.memo(MessageItem);

function MessageTextWithFileLinks({ html, rawContent, agentId }: { html: string; rawContent: string; agentId: string | null }) {
  const segments = parseFileLinks(rawContent);
  const hasPaths = segments.some(s => s.type === 'path');

  if (!hasPaths) {
    return <div className="message-text" dangerouslySetInnerHTML={{ __html: html }} />;
  }

  const paths = segments.filter(s => s.type === 'path').map(s => s.value);
  const escaped = paths.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`);
  const htmlParts = html.split(re);
  const pathSetLower = new Set(paths);

  return (
    <div className="message-text">
      {htmlParts.map((part, i) =>
        pathSetLower.has(part)
          ? <FileLink key={i} path={part} agentId={agentId} />
          : <span key={i} dangerouslySetInnerHTML={{ __html: part }} />
      )}
    </div>
  );
}

function ImageContent({ url }: { url: string }) {
  const [error, setError] = React.useState(false);

  // borgee #1108 F5: only render an <img src>/<a href> for URLs that pass the
  // same allowlist the server enforces at write time — http(s):// (any case)
  // OR a same-origin relative path with a single leading '/' (NOT '//', which
  // is protocol-relative and unsafe). Anything else (javascript:, data:, etc.)
  // is shown as inert plain text so a stored row can't become a phishing /
  // script-anchor vector. Required regardless of the server guard because the
  // server can't fix rows persisted before the fix.
  const isSafe = isAllowedImageContentURL(url);
  if (!isSafe) {
    return <span className="message-image-unsafe">{url}</span>;
  }

  if (error) {
    return (
      <div className="image-error">
        <span>Image failed to load</span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="image-fallback-link">
          {url}
        </a>
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt="uploaded image"
        className="message-image"
        onError={() => setError(true)}
        loading="lazy"
      />
    </a>
  );
}

function formatTime(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  if (isToday) return timeStr;
  if (isYesterday) return `昨天 ${timeStr}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${timeStr}`;
}
